import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Megaphone, ChevronLeft, ChevronRight, Pin, Info, AlertTriangle,
  Sparkles, Calendar, Wrench, X, Bell
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_CONFIG = {
  info: { 
    icon: Info, 
    color: 'text-blue-400', 
    bg: 'bg-blue-500/10',
    border: 'border-blue-500/30',
    label: 'Info'
  },
  warning: { 
    icon: AlertTriangle, 
    color: 'text-yellow-400', 
    bg: 'bg-yellow-500/10',
    border: 'border-yellow-500/30',
    label: 'Warning'
  },
  update: { 
    icon: Sparkles, 
    color: 'text-green-400', 
    bg: 'bg-green-500/10',
    border: 'border-green-500/30',
    label: 'Update'
  },
  event: { 
    icon: Calendar, 
    color: 'text-purple-400', 
    bg: 'bg-purple-500/10',
    border: 'border-purple-500/30',
    label: 'Event'
  },
  maintenance: { 
    icon: Wrench, 
    color: 'text-orange-400', 
    bg: 'bg-orange-500/10',
    border: 'border-orange-500/30',
    label: 'Maintenance'
  }
};

export const BulletinBoard = () => {
  const [bulletins, setBulletins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedBulletin, setSelectedBulletin] = useState(null);
  const [hasUnread, setHasUnread] = useState(false);

  useEffect(() => {
    loadBulletins();
  }, []);

  useEffect(() => {
    // Check for unread bulletins
    const readBulletins = JSON.parse(localStorage.getItem('readBulletins') || '[]');
    const unread = bulletins.some(b => !readBulletins.includes(b.id));
    setHasUnread(unread);
  }, [bulletins]);

  const loadBulletins = async () => {
    try {
      const response = await axios.get(`${API}/bulletin/active`);
      setBulletins(response.data.bulletins || []);
    } catch (error) {
      console.error('Failed to load bulletins:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = (bulletinId) => {
    const readBulletins = JSON.parse(localStorage.getItem('readBulletins') || '[]');
    if (!readBulletins.includes(bulletinId)) {
      readBulletins.push(bulletinId);
      localStorage.setItem('readBulletins', JSON.stringify(readBulletins));
    }
    // Update unread status
    const unread = bulletins.some(b => !readBulletins.includes(b.id) && b.id !== bulletinId);
    setHasUnread(unread);
  };

  const openBulletin = (bulletin) => {
    setSelectedBulletin(bulletin);
    markAsRead(bulletin.id);
  };

  if (loading || bulletins.length === 0) {
    return null; // Don't show if no bulletins
  }

  const readBulletins = JSON.parse(localStorage.getItem('readBulletins') || '[]');

  return (
    <>
      {/* Collapsed Toggle Button - Fixed on left side */}
      <motion.div
        initial={{ x: -100 }}
        animate={{ x: 0 }}
        className="fixed left-0 top-1/2 -translate-y-1/2 z-40"
      >
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={`flex items-center gap-2 px-3 py-4 rounded-r-lg transition-all ${
            isOpen 
              ? 'bg-babylon-gold text-babylon-void' 
              : 'bg-babylon-obsidian/90 border border-l-0 border-babylon-gold/30 hover:bg-babylon-gold/20'
          }`}
        >
          <div className="relative">
            <Megaphone className={`w-5 h-5 ${isOpen ? 'text-babylon-void' : 'text-babylon-gold'}`} />
            {hasUnread && !isOpen && (
              <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full animate-pulse" />
            )}
          </div>
          {isOpen ? (
            <ChevronLeft className="w-4 h-4" />
          ) : (
            <ChevronRight className="w-4 h-4 text-babylon-gold" />
          )}
        </button>
      </motion.div>

      {/* Expanded Bulletin Board Panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ x: -320, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -320, opacity: 0 }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed left-0 top-0 h-full w-80 bg-babylon-void/95 backdrop-blur-lg border-r border-babylon-gold/20 z-30 shadow-2xl"
          >
            {/* Header */}
            <div className="p-4 border-b border-babylon-gold/20 bg-gradient-to-r from-babylon-gold/10 to-transparent">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Megaphone className="w-5 h-5 text-babylon-gold" />
                  <h2 className="font-heading text-lg text-babylon-gold">Bulletin Board</h2>
                </div>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-1 hover:bg-babylon-gold/20 rounded transition-colors"
                >
                  <X className="w-4 h-4 text-babylon-text-dim" />
                </button>
              </div>
              <p className="text-xs text-babylon-text-dim mt-1">
                {bulletins.length} announcement{bulletins.length !== 1 ? 's' : ''}
              </p>
            </div>

            {/* Bulletins List */}
            <div className="p-4 space-y-3 overflow-y-auto h-[calc(100%-120px)]">
              {bulletins.map(bulletin => {
                const config = TYPE_CONFIG[bulletin.type] || TYPE_CONFIG.info;
                const TypeIcon = config.icon;
                const isRead = readBulletins.includes(bulletin.id);
                
                return (
                  <motion.div
                    key={bulletin.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    onClick={() => openBulletin(bulletin)}
                    className={`p-3 rounded-lg border cursor-pointer transition-all hover:scale-[1.02] ${config.bg} ${config.border} ${
                      !isRead ? 'ring-1 ring-babylon-gold/50' : ''
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      {bulletin.pinned && (
                        <Pin className="w-3 h-3 text-babylon-gold flex-shrink-0 mt-0.5" />
                      )}
                      <TypeIcon className={`w-4 h-4 flex-shrink-0 mt-0.5 ${config.color}`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className={`text-sm font-medium truncate ${!isRead ? 'text-babylon-gold' : 'text-babylon-text'}`}>
                            {bulletin.title}
                          </h3>
                          {!isRead && (
                            <span className="w-2 h-2 bg-babylon-gold rounded-full flex-shrink-0" />
                          )}
                        </div>
                        <p className="text-xs text-babylon-text-dim line-clamp-2 mt-1">
                          {bulletin.content}
                        </p>
                        <p className="text-[10px] text-babylon-text-dim/60 mt-2">
                          {new Date(bulletin.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {/* Footer */}
            <div className="absolute bottom-0 left-0 right-0 p-3 border-t border-babylon-gold/20 bg-babylon-void/80">
              <p className="text-[10px] text-babylon-text-dim text-center">
                📢 Stay updated with the latest news
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Bulletin Detail Modal */}
      <AnimatePresence>
        {selectedBulletin && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedBulletin(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="glass-card max-w-md w-full max-h-[80vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {(() => {
                const config = TYPE_CONFIG[selectedBulletin.type] || TYPE_CONFIG.info;
                const TypeIcon = config.icon;
                
                return (
                  <>
                    <div className={`p-4 border-b border-babylon-gold/20 ${config.bg}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <TypeIcon className={`w-5 h-5 ${config.color}`} />
                          <span className={`text-xs px-2 py-0.5 rounded ${config.bg} ${config.color} border ${config.border}`}>
                            {config.label}
                          </span>
                          {selectedBulletin.pinned && (
                            <Pin className="w-4 h-4 text-babylon-gold" />
                          )}
                        </div>
                        <button 
                          onClick={() => setSelectedBulletin(null)}
                          className="text-babylon-text-dim hover:text-white"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                      <h3 className="font-heading text-xl text-babylon-gold mt-3">
                        {selectedBulletin.title}
                      </h3>
                    </div>
                    
                    <div className="p-4">
                      <p className="text-sm text-babylon-text whitespace-pre-wrap leading-relaxed">
                        {selectedBulletin.content}
                      </p>
                      
                      <div className="mt-6 pt-4 border-t border-babylon-gold/10 flex items-center justify-between text-xs text-babylon-text-dim">
                        <span>Posted by {selectedBulletin.created_by_name}</span>
                        <span>{new Date(selectedBulletin.created_at).toLocaleString()}</span>
                      </div>
                    </div>
                  </>
                );
              })()}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default BulletinBoard;
